<script lang="ts">
  import { onMount } from 'svelte';
  import { atEdge, ceiling, floor, stride, type GameEvent } from '../../core/engine';
  import { nextPrime } from '../../core/numbers';
  import { sound } from '../../shared/lib/audio';
  import { fmt } from '../../shared/lib/format';
  import { game } from '../../shared/lib/game.svelte';
  import { settings } from '../../shared/lib/settings.svelte';

  const TICKS = 72;

  let dial: HTMLButtonElement;
  let halo: HTMLElement;
  let ripple: HTMLElement;
  let canvas: HTMLCanvasElement;

  const s = $derived(game.s);
  const blocked = $derived(atEdge(s));
  const step = $derived(stride(s));
  const sign = $derived(s.direction === 1 ? '+' : '−');

  /** How far along the current horizon you have explored, in the direction of travel. */
  const progress = $derived(
    s.direction === 1
      ? Math.min(1, s.maxVisited / ceiling(s))
      : floor(s) < 0
        ? Math.min(1, s.minVisited / floor(s))
        : 0,
  );
  const lit = $derived(Math.round(progress * TICKS));

  /** Radar only: how close the next prime is (0 = just left one, 1 = arriving). */
  const primeArc = $derived.by(() => {
    if (s.upgrades.radar === 0 || s.position < 1) return null;
    const ahead = nextPrime(s.position, 1);
    const behind = nextPrime(s.position + 1, -1);
    if (ahead === null || behind === null) return null;
    const span = ahead - behind;
    return s.direction === 1 ? (s.position - behind) / span : (ahead - s.position) / span;
  });

  function press(e: PointerEvent) {
    if (e.button !== 0) return;
    e.preventDefault();
    sound.unlock();
    game.tap();
    if (!settings.reducedMotion) {
      ripple.animate(
        [
          { transform: 'scale(0.92)', opacity: 0.35 },
          { transform: 'scale(1.28)', opacity: 0 },
        ],
        { duration: 520, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      );
    }
  }

  /** Keyboard activation (Enter/Space while focused) arrives as a click with detail 0. */
  function keyClick(e: MouseEvent) {
    if (e.detail === 0) {
      sound.unlock();
      game.tap();
    }
  }

  // ----- celebration: halo pulse + a small spark burst -----
  interface Spark {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
  }
  let sparks: Spark[] = [];
  let sparkColor = '#fff';
  let raf = 0;

  // Read the palette's colours once per palette change, not on every prime (that forces a
  // style recalculation mid-tap).
  let tones = { prime: '#fff', twin: '#fff', famous: '#fff' };
  $effect(() => {
    void settings.palette;
    requestAnimationFrame(() => {
      const cs = getComputedStyle(dial);
      tones = {
        prime: cs.getPropertyValue('--prime').trim(),
        twin: cs.getPropertyValue('--twin').trim(),
        famous: cs.getPropertyValue('--famous').trim(),
      };
    });
  });

  let lastBurst = 0;

  function burst(tone: 'prime' | 'twin' | 'famous', count: number) {
    if (settings.reducedMotion) return;
    // Plain primes come thick and fast at high speed; keep the effect special.
    const now = performance.now();
    if (tone === 'prime' && now - lastBurst < 450) return;
    lastBurst = now;
    sparkColor = tones[tone];
    canvas.style.visibility = 'visible';
    halo.style.setProperty('--halo', `var(--${tone})`);
    halo.animate(
      [
        { transform: 'scale(1)', opacity: 0.9 },
        { transform: 'scale(1.16)', opacity: 0 },
      ],
      { duration: 700, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
    const w = canvas.width;
    const r = w * 0.3;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const v = 1.2 + Math.random() * 1.8;
      sparks.push({
        x: w / 2 + Math.cos(a) * r,
        y: w / 2 + Math.sin(a) * r,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        life: 1,
      });
    }
    if (!raf) raf = requestAnimationFrame(draw);
  }

  function draw() {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = sparkColor;
    const dpr = Math.min(devicePixelRatio || 1, 2);
    sparks = sparks.filter((p) => (p.life -= 0.028) > 0);
    for (const p of sparks) {
      p.x += p.vx * dpr;
      p.y += p.vy * dpr;
      p.vx *= 0.96;
      p.vy *= 0.96;
      ctx.globalAlpha = p.life;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.2 * dpr * p.life + 0.6, 0, Math.PI * 2);
      ctx.fill();
    }
    raf = sparks.length ? requestAnimationFrame(draw) : 0;
    if (!raf) canvas.style.visibility = 'hidden';
  }

  function onEvents(events: readonly GameEvent[]) {
    let tone: 'prime' | 'twin' | 'famous' | null = null;
    for (const e of events) {
      if (e.type === 'twin') tone = 'twin';
      else if (e.type === 'famous' && tone !== 'twin') tone = 'famous';
      else if (e.type === 'prime' && !tone) tone = 'prime';
    }
    if (tone) burst(tone, tone === 'prime' ? 12 : 22);
  }

  onMount(() => {
    const size = () => {
      const px = Math.round(
        canvas.getBoundingClientRect().width * Math.min(devicePixelRatio || 1, 2),
      );
      canvas.width = canvas.height = px;
    };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(canvas);
    const off = game.on(onEvents);
    return () => {
      off();
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  });
</script>

<div class="wrap">
  <canvas bind:this={canvas} class="sparks" aria-hidden="true"></canvas>
  <span bind:this={halo} class="halo" aria-hidden="true"></span>

  <button
    bind:this={dial}
    class="dial"
    class:blocked
    onpointerdown={press}
    onclick={keyClick}
    aria-label={blocked ? 'At the horizon' : `Step ${sign}${step}`}
  >
    <svg class="ring" viewBox="0 0 200 200" aria-hidden="true">
      {#each { length: TICKS } as _, i (i)}
        <line
          x1="100"
          y1="6"
          x2="100"
          y2={i % 6 === 0 ? 17 : 13}
          class:on={i < lit}
          transform="rotate({(i * 360) / TICKS} 100 100)"
        />
      {/each}
    </svg>
    {#if primeArc !== null}
      <svg class="ring arcs" viewBox="0 0 200 200" aria-hidden="true">
        <circle class="track" cx="100" cy="100" r="80" pathLength="100" />
        <circle
          class="arc"
          cx="100"
          cy="100"
          r="80"
          pathLength="100"
          stroke-dasharray="{primeArc * 100} 100"
          transform="rotate(-90 100 100)"
        />
      </svg>
    {/if}

    <span class="face">
      <span bind:this={ripple} class="ripple" aria-hidden="true"></span>
      {#if blocked}
        <span class="label small">Horizon</span>
        <span class="sub">{fmt(s.direction === 1 ? ceiling(s) : floor(s))}</span>
      {:else}
        <span class="label">{sign}{step}</span>
        <span class="sub">tap<span class="kbd">&nbsp;·&nbsp;space</span></span>
      {/if}
    </span>
  </button>
</div>

<style>
  .wrap {
    --dial: clamp(184px, 58vw, 252px);
    position: relative;
    width: var(--dial);
    height: var(--dial);
    flex: none;
  }

  .sparks {
    visibility: hidden;
    position: absolute;
    inset: -30%;
    width: 160%;
    height: 160%;
    pointer-events: none;
  }

  .halo {
    position: absolute;
    inset: 9%;
    border-radius: 50%;
    border: 2px solid var(--halo, var(--prime));
    opacity: 0;
    pointer-events: none;
  }

  .dial {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    display: grid;
    place-items: center;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }

  .dial:focus-visible {
    outline-offset: -4px;
    border-radius: 50%;
  }

  .ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    /* Separate layers: the arc changes every step, the ticks almost never. */
    will-change: transform;
  }

  .ring line {
    stroke: var(--line);
    stroke-width: 2;
    stroke-linecap: round;
    transition: stroke var(--t-slow) var(--ease-out);
  }
  .ring line.on {
    stroke: var(--accent);
  }

  .track,
  .arc {
    fill: none;
    stroke-width: 3;
  }
  .track {
    stroke: color-mix(in srgb, var(--prime) 14%, transparent);
  }
  .arc {
    stroke: var(--prime);
    stroke-linecap: round;
    transition: stroke-dasharray var(--t) var(--ease-out);
  }

  .face {
    position: relative;
    width: 72%;
    height: 72%;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    background: radial-gradient(
      circle at 50% 30%,
      color-mix(in srgb, var(--accent) 16%, var(--bg-raised)),
      var(--bg-raised) 70%
    );
    border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--line));
    box-shadow:
      0 1px 0 color-mix(in srgb, white 30%, transparent) inset,
      0 10px 30px -12px color-mix(in srgb, var(--accent) 45%, transparent),
      0 2px 6px -2px rgb(0 0 0 / 0.18);
    transition:
      transform var(--t-fast) var(--ease-out),
      box-shadow var(--t) var(--ease-out);
  }

  .dial:active .face {
    transform: scale(0.955);
    box-shadow:
      0 1px 0 color-mix(in srgb, white 20%, transparent) inset,
      0 4px 14px -8px color-mix(in srgb, var(--accent) 50%, transparent);
  }

  .ripple {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid var(--accent);
    opacity: 0;
    pointer-events: none;
  }

  .label {
    font-family: var(--font-num);
    font-weight: var(--num-weight);
    font-size: clamp(2rem, 10vw, 2.75rem);
    line-height: 1;
    color: var(--accent);
    letter-spacing: -0.02em;
  }
  .label.small {
    font-family: var(--font-ui);
    font-size: var(--text-lg);
    font-weight: 650;
    color: var(--ink-2);
  }

  .sub {
    font-size: var(--text-xs);
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-3);
  }

  .kbd {
    display: none;
  }
  @media (hover: hover) and (pointer: fine) {
    .kbd {
      display: inline;
    }
  }

  .blocked .face {
    background: var(--bg-sunken);
    border-style: dashed;
    box-shadow: none;
  }
</style>
